/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { B2B } from '@/components/blocks/B2B';
import { TYPES } from '@/data/constants/product';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { productIsA } from '@/utils/productIsA';
import { Alert, Box, Button, IconButton, Snackbar } from '@mui/material';
import {
	FC,
	Fragment,
	MouseEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { useWishLists } from '@/data/Content/WishLists';
import { ModalLoginFavorites } from '@/components/content/Cart/parts/ModalLoignFavorites';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { snackBarSX } from '@/components/content/Notifications/styles/snackbar';
import { alertSX } from '@/components/content/Notifications/styles/alert';
import { useUser } from '@/data/User';
import { isNotAuthorizedError } from '@/data/utils/isNotAuthorizedError';
type Props = {
	standalone?: boolean;
	isIconFavorite?: boolean;
	inThumbnail?: boolean;
	disabled?: boolean;
};
enum DRAWER_AUTH {
	AUTH = 'auth',
	LOGIN = 'login',
	REGISTER = 'register',
}

export const ProductDetailsAddToWishList: FC<Props> = ({
	standalone = false,
	isIconFavorite = false,
	inThumbnail = false,
	disabled: _disabled,
}) => {
	const { selection, loginStatus, wishLists, addToWishList, product } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails> & ReturnType<typeof useSkuListTable>;
	const isBundle = productIsA(product, TYPES.bundle);
	const sku = !isBundle ? selection?.sku : product;
	const buyable = !isBundle ? selection?.buyable : true;
	const quantity = !isBundle ? selection?.quantity : 1;
	const { prices } = getProductDisplayInfo(sku, product);
	const disabled = !buyable || !prices?.offer;
	const dtId = useMemo(() => `wishlist-selection${standalone ? '-standalone' : ''}`, [standalone]);
	const [drawerAuth, setDrawerAuth] = useState<DRAWER_AUTH | null>(null);
	const isMobile = useIsMobile();
	const router = useNextRouter();
	const RouteLocal = useLocalization('Routes');
	const handleOnDrawerAuthClear = () => {
		setDrawerAuth(null);
	};
	const handleGoRegister = () => {
		setDrawerAuth(null);
		router.push(RouteLocal.Registration.route.t());
	};

	const [message, setMessage] = useState('');

	const { favoritesWishList, mutateWishLists } = useWishLists();
	const { mutateUser } = useUser();
	const isInFavorites = !!favoritesWishList?.item?.find(
		(item) => item.partNumber.split('-').slice(0, 2).join('-') === product?.partNumber
	);
	const [inWishList, setInWishList] = useState(false);
	useEffect(() => {
		setInWishList(isInFavorites);
	}, [isInFavorites]);

	const onClickFavorites = useCallback(
		(partNumber = '', quantity: number) =>
			async (_event: MouseEvent<HTMLElement>) => {
				if (!loginStatus) {
					setDrawerAuth(DRAWER_AUTH.LOGIN);
				} else {
					const wishListFavorites = wishLists.find((elm) => elm.description === 'Favoritos');
					if (wishListFavorites) {
						try {
							setInWishList(true);
							await addToWishList(partNumber, quantity, wishListFavorites)();
							setMessage('El artículo se ha añadido correctamente a la lista');
							mutateWishLists();
						} catch (e) {
							if (isNotAuthorizedError(e)) {
								setInWishList(false);
								await mutateUser();
								setDrawerAuth(DRAWER_AUTH.LOGIN);
								return;
							}
							throw e;
						}
					}
				}
			},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[addToWishList, loginStatus, wishLists, mutateWishLists, mutateUser]
	);
	const onClear = () => {
		setMessage('');
	};
	return (
		<Fragment>
			<Snackbar
				open={message ? true : false}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				autoHideDuration={5000}
				onClose={onClear}
				ClickAwayListenerProps={{
					onClickAway: onClear,
					mouseEvent: 'onMouseDown',
				}}
				sx={snackBarSX}
			>
				<Box>
					<Box>
						<Alert severity={'success'} onClose={onClear} sx={alertSX} icon={false}>
							{message}
						</Alert>
					</Box>
				</Box>
			</Snackbar>
			<B2B is={false}>
				<>
					{isIconFavorite ? (
						<IconButton
							onClick={onClickFavorites(sku?.partNumber, quantity)}
							id="pdp-favorite-button"
							aria-label="fingerprint"
							sx={{
								backgroundColor: '#F1F1F1',
								position: 'absolute',
								bottom: inThumbnail ? '115px' : '65px',
								left: inThumbnail ? undefined : '19px',
								right: inThumbnail ? '19px' : undefined,
								zIndex: 1001,
							}}
							disabled={_disabled}
						>
							{inWishList ? <Favorite sx={{ color: 'primary.main' }} /> : <FavoriteBorder />}
						</IconButton>
					) : (
						<Button
							data-testid={dtId}
							id={dtId}
							onClick={onClickFavorites(sku?.partNumber, quantity)}
							disabled={disabled}
							sx={{
								background: '#F1F1F1',
								borderRadius: '22px',
								color: '#2C2C2C',
								fontSize: 'var(--font-size-body)',
								fontWeight: 'bold',
								height: '50px',
								minWidth: '153px',
							}}
							startIcon={<FavoriteBorderIcon />}
						>
							{'Favoritos'}
						</Button>
					)}
					{!isMobile && DRAWER_AUTH.LOGIN === drawerAuth ? (
						<ModalLoginFavorites
							onClose={handleOnDrawerAuthClear}
							onGoRegister={handleGoRegister}
						/>
					) : null}
				</>
			</B2B>
		</Fragment>
	);
};
